/**
 * NoteSlidesViewer — interactive revision-card deck for the "Slides" notes
 * type.
 *
 * Notes generated as type=slides store structured {title, key_concepts[],
 * example} cards (see backend/services/ai_service.py generate_slide_cards)
 * instead of one long HTML document. This renders that deck as a simple,
 * flippable card-by-card viewer so a student can work through key ideas one
 * at a time — read the concept, try to recall an example themselves, then
 * reveal the real one — rather than scrolling a wall of text.
 *
 * Props:
 *   - cards: [{ title, key_concepts: string[], example?: string }]
 *   - className: optional extra container classes.
 */
import React, { useState } from 'react';
import KaTeXContent from '../common/KaTeXContent';

export default function NoteSlidesViewer({ cards, className = '' }) {
  const deck = Array.isArray(cards) ? cards.filter(Boolean) : [];
  const [index, setIndex] = useState(0);
  const [showExample, setShowExample] = useState(false);

  if (deck.length === 0) {
    return null;
  }

  const total = deck.length;
  const card = deck[Math.min(index, total - 1)];

  const goTo = (nextIndex) => {
    setIndex(Math.max(0, Math.min(total - 1, nextIndex)));
    setShowExample(false);
  };

  return (
    <div className={`bg-gradient-to-br from-blue-900 via-sky-800 to-fuchsia-800 p-6 sm:p-10 flex flex-col gap-4 text-white rounded-2xl ${className}`}>
      <div className="flex items-center justify-between text-xs opacity-80">
        <span>🗂️ Revision Cards · Card {index + 1} / {total}</span>
      </div>

      <div
        className="h-1.5 w-full rounded-full bg-white/15 overflow-hidden"
        role="progressbar"
        aria-valuenow={index + 1}
        aria-valuemin={1}
        aria-valuemax={total}
      >
        <div
          className="h-full rounded-full bg-gradient-to-r from-emerald-400 to-sky-400 transition-all duration-300"
          style={{ width: `${((index + 1) / total) * 100}%` }}
        />
      </div>

      <div className="bg-white/10 rounded-xl p-4 sm:p-6 flex flex-col gap-4 min-h-[220px]">
        <div className="text-lg sm:text-2xl font-bold leading-snug">
          <KaTeXContent content={card.title} className="inline" />
        </div>

        <ul className="flex flex-col gap-2">
          {(card.key_concepts || []).map((concept, i) => (
            <li key={i} className="flex items-start gap-2 text-sm sm:text-base">
              <span aria-hidden="true" className="mt-0.5">💡</span>
              <KaTeXContent content={concept} className="inline flex-1" />
            </li>
          ))}
        </ul>

        {card.example && (
          showExample ? (
            <div className="bg-black/30 rounded-xl p-3 text-sm sm:text-base">
              <span className="font-semibold">🧩 Example: </span>
              <KaTeXContent content={card.example} className="inline" />
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setShowExample(true)}
              className="self-start text-xs sm:text-sm px-3 py-1.5 rounded-full border border-white/30 hover:border-white/60"
            >
              🧩 Tap to reveal an example
            </button>
          )
        )}
      </div>

      <div className="flex items-center justify-between gap-2 mt-1">
        <button
          type="button"
          onClick={() => goTo(index - 1)}
          disabled={index === 0}
          className="text-sm px-4 py-1.5 rounded-full border border-white/30 hover:border-white/60 disabled:opacity-30 disabled:cursor-not-allowed"
        >
          ← Prev
        </button>
        <div className="flex items-center gap-1.5" aria-hidden="true">
          {deck.map((_, i) => (
            <span
              key={i}
              className={`h-1.5 w-1.5 rounded-full ${i === index ? 'bg-white' : 'bg-white/30'}`}
            />
          ))}
        </div>
        {index === total - 1 ? (
          <button
            type="button"
            onClick={() => goTo(0)}
            className="text-sm px-4 py-1.5 rounded-full bg-white text-gray-900 font-semibold"
          >
            Restart
          </button>
        ) : (
          <button
            type="button"
            onClick={() => goTo(index + 1)}
            className="text-sm px-4 py-1.5 rounded-full bg-white text-gray-900 font-semibold"
          >
            Next →
          </button>
        )}
      </div>
    </div>
  );
}
